import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { initAnalytics } from "@/lib/analytics";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import EmailVerificationBanner from "@/components/EmailVerificationBanner";
import LandingPage from "@/pages/LandingPage";
import OffersPage from "@/pages/OffersPage";
import OfferDetailPage from "@/pages/OfferDetailPage";
import CityLandingPage from "@/pages/CityLandingPage";
import HubLandingPage from "@/pages/HubLandingPage";
import CityComboPage from "@/pages/CityComboPage";
import BrowseHubPage from "@/pages/BrowseHubPage";
import ListOfferPage from "@/pages/ListOfferPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import FavoritesPage from "@/pages/FavoritesPage";
import AdminPage from "@/pages/AdminPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ImprintPage from "@/pages/ImprintPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AboutPage from "@/pages/AboutPage";
import NotFoundPage from "@/pages/NotFoundPage";
import CookieBanner from "@/components/CookieBanner";
import ScrollToTop from "@/components/ScrollToTop";
import { Toaster } from "sonner";

const PRODUCTION_DOMAIN = "https://doing-good.org";

// Sitewide structured data — added once at app mount.
function SitewideSchema() {
  useEffect(() => {
    const upsert = (id, obj) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("script");
        el.id = id;
        el.type = "application/ld+json";
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(obj);
    };
    upsert("ld-organization", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "doing-good.org",
      alternateName: "doing-good",
      url: PRODUCTION_DOMAIN,
      logo: `${PRODUCTION_DOMAIN}/logo-square.svg`,
      image: `${PRODUCTION_DOMAIN}/og-default-v2.png`,
      description:
        "doing-good.org is Europe's open directory of pro bono opportunities — donations, volunteering, pro bono services and impact investments with real local NGOs across 9 countries.",
      areaServed: ["Germany", "Austria", "Switzerland", "Belgium", "Luxembourg", "Netherlands", "Italy", "France", "Spain"],
      knowsLanguage: ["en", "de", "fr", "es"],
      founder: {
        "@type": "Person",
        name: "David Schindler",
        url: "https://www.linkedin.com/in/david-schindler-1a122a313",
      },
      sameAs: [
        "https://www.linkedin.com/in/david-schindler-1a122a313",
      ],
    });
    upsert("ld-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      url: PRODUCTION_DOMAIN,
      name: "doing-good.org",
      inLanguage: ["en", "de"],
      publisher: { "@type": "Organization", name: "doing-good.org", url: PRODUCTION_DOMAIN },
      potentialAction: {
        "@type": "SearchAction",
        target: `${PRODUCTION_DOMAIN}/offers?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });
  }, []);
  return null;
}

function App() {
  useEffect(() => {
    // Bootstraps Plausible only on doing-good.org AND only after the user
    // has accepted analytics in the existing cookie banner. Idempotent.
    initAnalytics();
  }, []);
  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <ScrollToTop />
        <SitewideSchema />
        <AuthProvider>
          <EmailVerificationBanner />
          <Header />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/offers" element={<OffersPage />} />
              <Route path="/offer/:id" element={<OfferDetailPage />} />
              {/* SEO hub pages */}
              <Route path="/c/:slug" element={<CityLandingPage />} />
              <Route path="/c/:citySlug/:tagSlug" element={<CityComboPage />} />
              <Route path="/cause/:slug" element={<HubLandingPage kind="cause" />} />
              <Route path="/country/:slug" element={<HubLandingPage kind="country" />} />
              <Route path="/category/:slug" element={<HubLandingPage kind="category" />} />
              <Route path="/causes" element={<BrowseHubPage kind="cause" />} />
              <Route path="/countries" element={<BrowseHubPage kind="country" />} />
              <Route path="/categories" element={<BrowseHubPage kind="category" />} />
              <Route path="/cities" element={<BrowseHubPage kind="city" />} />
              {/* App pages */}
              <Route path="/list-offer" element={<ListOfferPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/favorites" element={<FavoritesPage />} />
              <Route path="/admin" element={<AdminPage />} />
              {/* Legal pages (GDPR + §5 TMG) */}
              <Route path="/impressum" element={<ImprintPage />} />
              <Route path="/imprint" element={<ImprintPage />} />
              <Route path="/datenschutz" element={<PrivacyPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/ueber-uns" element={<AboutPage />} />
              {/* 404 catch-all */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </main>
          <Footer />
          <CookieBanner />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
